// A surface card's name (renamed for this job by tapping it) and its
// non-standard paint flag with a note for the estimator (#412,
// docs/SURFACE-NAMES.md). The paint itself is chosen on the desk's Take Off;
// the label shows a red "?" until the desk resolves it (another paint or paint systems), then a tick (#666).
import { useState } from 'react';
import { surfaceLabel, type MeasuredSurface, type PaintFlag, type PaintStates } from '@higgins/m2q-pricing';
import { PaintStatePill } from './paint-completeness';
import { SavedTextField, TEXT_MAX } from './saved-text-field';

interface SurfaceNameProps {
  surface: MeasuredSurface;
  sprayed: boolean;
  /** Its non-standard paint flag and the desk's paint systems, each done or not. */
  paintStates: PaintStates;
  onRename: (name: string) => void;
}

/** The card's name: a tap opens it for typing, saved as typed; leaving the field closes it (blank goes back to the type's name). */
export function SurfaceName({ surface, sprayed, paintStates, onRename }: SurfaceNameProps) {
  const [editing, setEditing] = useState(false);
  const label = surfaceLabel(surface);
  if (editing) {
    return (
      <SavedTextField
        value={label}
        autoFocus
        maxLength={TEXT_MAX}
        label={`Name for ${surface.typeName}`}
        placeholder={surface.typeName}
        className="min-w-0 flex-1 bg-white px-2 text-base md:text-base"
        onSave={onRename}
        onDone={() => setEditing(false)}
      />
    );
  }
  return (
    <span className="min-w-0">
      <button type="button" onClick={() => setEditing(true)} aria-label={`Rename ${label}`} className="min-h-11 border-b border-dashed border-slate-400 text-left font-medium text-slate-900">
        {label}
      </button>
      {surface.name && <span className="ml-2 text-xs text-slate-500">{surface.typeName}</span>}
      {sprayed && <span className="ml-2 rounded-full bg-sky-50 px-2 text-xs font-normal text-sky-800">spray</span>}
      <PaintStatePill states={paintStates} className="ml-2" />
    </span>
  );
}

/** In the card's Settings: the switch, and the note once it is on. */
export function PaintFlagSetting({ flag, onChange }: { flag: PaintFlag | undefined; onChange: (flag: PaintFlag | undefined) => void }) {
  const on = flag !== undefined;
  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" role="switch" checked={on} onChange={(e) => onChange(e.target.checked ? {} : undefined)} className="h-5 w-5 accent-orange-500" />
        Non-standard paint
      </label>
      {on && (
        <SavedTextField
          value={flag.note ?? ''}
          maxLength={TEXT_MAX}
          label="Note for the estimator"
          placeholder="Note for the estimator, e.g. Durebuild + Weathermax per spec"
          className="h-11 min-w-0 flex-1 basis-64 bg-white px-2 text-sm"
          onSave={(note) => onChange({ note })}
        />
      )}
    </div>
  );
}
